import api from '@/services/api';
import { fetchChartData } from '@/helpers/chart';
import { cloneDeep } from 'lodash';
import { getMinSampling } from "@/helpers/measure";

var VueCookie = require('vue-cookie');

const initialState = () => {
  return {
    all: [],
    current: { users: [], sites: [] },
    selected: { users: [], sites: [] },
    editFeedback: { show: false },
    tree: null,
    treeNodes: {},
    selectedMeasureChannels: [],
    selectedMeasureParameters: [],
    selectedIndicatorChannels: [],
    selectedIndicatorParameters: [],
    lastFetchedChartParameters: {},
    lastFetchedChartData: {},
    measureTypes: [],
    selectedMeasureTypes: [],
    selectedSources: [],
    dataModelId: 1
    /* example source object:{
      name: "<source_name>" //example: POD
      type: "<source_type>" //example: channel
      code: "<source_code>"
      measures: ["<list_of_measure_types_as_string>] //only for channels
    }*/
  }
};

// getters
const getters = {
  getMeasureTypeByCode: (state) => (code) => {
    if (!state.measureTypes) return null;
    return state.measureTypes.find((type) => type.code === code);
  },

  // GETTER: gets tree node that matches code
  getNodeByCode: (state) => (code) => {
    if (!state.tree) return null;
    let returnNode = null;
    state.tree.children.some(function s(node) {
      if (node.code === code) {
        returnNode = node
        return true;
      }
      else if (node.children && node.children.length > 0) {
        return node.children.some(s)
      }
      return false;
    })
    return returnNode;
  },

  getExpandedNodes: (state) => {
    if (!state.tree) return [];
    return state.tree.children.reduce(function s(list, node) {
      if (node.open) {
        list.push(node.code)
      }
      if (node.children && node.children.length > 0) {
        list.push(...node.children.reduce(s, []))
      }
      return list
    }, [])
  },

  getDefaultMeasureTypeByCode: (state) => (code) => {
    if (!state.measureTypes) return null;
    let MT = state.measureTypes.find((type) => type.code === code);
    //return empty array if not found
    if (!MT || !MT.measureTypes || MT.measureTypes.length === 0) return null
    //return energy if found in measure types
    else if (MT.measureTypes.ENERGY) return ['ENERGY']
    //return energy as default
    else return null
  },

  getMinSampling: (state) => () =>
    getMinSampling(state.selectedMeasureTypes.map((type) => type.minSampling)),

  allMeasureTypes: (state) => {
    const allMeasureTypes = state.measureTypes
      .map((measureType) => measureType.measureTypes || {});
    const allMeasureTypesKeys = allMeasureTypes.map((type) => Object.keys(type));

    const uniqueMeasureTypes = allMeasureTypesKeys
      .reduce((resultTypes, types) => [...resultTypes, ...types.reduce((typeAccumutator, type) =>
        resultTypes.includes(type) ? typeAccumutator : [...typeAccumutator, type]
        , [])]
        , []
      );

    return uniqueMeasureTypes.map((type) => {
      const typeEntity = allMeasureTypes.find((searchType) => searchType[type]);

      return {
        key: type,
        title: typeEntity[type],
      };
    });
  },
  // GETTER: returns complete measureTypes list
  measureTypes: (state) => {
    return state.measureTypes
  },
}

// actions
const actions = {
  //FUNCTION: Selects And Shows All Nodes from Passed Array in Source Tree
  selectAndShowNodes({ dispatch, commit, state }, payload) {
    if (!state.tree) return;
    state.tree.children.reduce(function s(hasSelected, node) {
      if (payload.includes(node.code)) {
        dispatch('setSelectState', { node, select: true })
        return true;
      }
      if (node.children && node.children.length > 0) {
        if (node.children.reduce(s, false)) {
          dispatch('setExpandState', { node, expand: true })
          return true
        }
      }
      return false || hasSelected;
    }, false)
  },
  //FUNCTION: Sets A Node's Visibility in Source Tree
  setVisibilityState({ commit }, payload) {
    commit('setVisibilityState', payload)
  },
  //FUNCTION: Expands or Collapses A Node in Source Tree
  toggleExpandState({ commit }, node) {
    commit('toggleExpandState', node)
  },
  //FUNCTION: Sets Expand State Of A Node in Source Tree Based On Value Recieved
  setExpandState({ commit }, { node, expand }) {
    commit('setExpandState', { node, expand })
  },
  //FUNCTION: Selects or Deselects A Node in Source Tree
  toggleSelectState({ commit, getters }, node) {
    commit('toggleSelectState', node)
    commit('toggleNodeInSelectedSources', {
      node,
      defaultMT: getters.getDefaultMeasureTypeByCode(node.code)
    })
  },
  //FUNCTION: Sets Select State Of A Node in Source Tree Based On Value Recieved
  setSelectState({ commit, getters }, { node, select }) {
    commit('setSelectState', { node, select })
    commit('toggleNodeInSelectedSources', {
      node,
      defaultMT: select ? getters.getDefaultMeasureTypeByCode(node.code) : null
    })
  },
  //FUNCTION: Filters the 'selectedSources' list based on current mode (MEASURES/INDICATORS)
  filterSelectedSourcesForMode({ commit }, filters) {
    commit('filterSelectedSourcesForMode', filters)
  },
  //FUNCTION: Deselects All Nodes in Source Tree and Clears the 'selectedSources' list
  clearAllSelectedSources({ commit }) {
    commit('unselectAllNodes')
    commit('clearAllSelectedSources')
  },
  //FUNCTION: Sets the 'selectedSources' list
  setSelectedSources({ commit }, payload) {
    commit('setSelectedSources', payload)
  },
  //FUNCTION: Deselects a Node in Source Tree and Removes it from the 'selectedSources' list
  removeSource({ commit, state }, source) {
    state.tree.children.some(function s(node) {
      if (node.code === source.code) {
        commit('toggleSelectState', node)
        commit('toggleNodeInSelectedSources', { node })
        return true
      }
      return node.children && node.children.some(s)
    })
  },
  //FUNCTION: Sets the 'measures' list in a source of 'channel' type in 'selectedSources' list
  setMeasuresInSelectedSource({ commit }, payload) {
    commit('setMeasuresInSelectedSource', payload)
  },
  //FUNCTION: Sets the 'tree' object (for sources) and selects all nodes currently present in the 'selectedSources' list
  setTree({ commit, state, getters }, tree) {
    commit('setTree', tree)
    if (state.selectedSources.length > 0) {
      state.selectedSources.forEach(s => {
        let node = getters.getNodeByCode(s.code)
        if (!!node) node.selected = true;
      })
    }
  },
  setModelId({ commit }, dataModelId) {
    commit('setModelId', dataModelId);
  },

  ////////OLD STATE ACTIONS////////
  clear({ commit }) {
    commit('clear');
  },

  // FUNCTION: loads measure types from API. Returns promise that resolves when measures are loaded
  async loadMeasureTypes({ commit, dispatch, rootState }) {
    return await api().post('/measure-types').then(({ data }) => {

      const measureTypes = data && data.data && data.data.measureTypes ? data.data.measureTypes : [];
      commit('setMeasureTypes', measureTypes);
    });
  },

  async get({ commit, state }, rawParams) {
    try {

      const params = cloneDeep(rawParams);

      //this part is skipped when fetching data for reports
      //this part is skipped if params.sources are already populated by reports
      //this part is also skipped if MODE is "INDICATORS"

      // console.log({ ...params })

      const sources = [];
      if (params.sources && params.sources.length === 0 && params.mode !== "INDICATORS") {
        // console.log("!!")
        state.selectedSources.filter(s => s.type === "channel").forEach(s => {
          if (!s.measures) {
            return sources.push({
              sourceCode: s.code,
              sourceType: s.type.toUpperCase(),
              measureType: null,
              dataModelId: s.dataModelId,
              //minSampling: '1'
            })
          }
          s.measures.forEach(m => {
            sources.push({
              sourceCode: s.code,
              sourceType: s.type.toUpperCase(),
              measureType: m,
              dataModelId: s.dataModelId,
              //minSampling: '1'
            })
          })
        })

        params.parameters.forEach((parameter) => {
          sources.push({
            sourceCode: parameter,
            sourceType: 'PARAMETER',
            measureType: null,
          });
        });

        params.channels = [];
        params.parameters = [];
        params.sources = sources;
      }

      if (params.sources.length > 0) {
        /**
         * clearly this is bad, we need a refactoring on backend
         * in order to use mixed data models
         */
        params.dataModelId = params.sources[0].dataModelId;
      }

      const data = await fetchChartData(
        params,
        // state.lastFetchedChartParameters,
        // state.lastFetchedChartData,
      );

      commit('setLastFetchedChartParameters', params);
      commit('setLastFetchedChartData', data);

      return data;
    } catch (error) {
      console.log("Error loading chart data");
      return null
    }
  },

  slots({ commit }, params) {
    return new Promise((resolve) => {
      api().post('/data-slots', params).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  tep({ commit }, params) {
    return new Promise((resolve, reject) => {
      api().post("/data/tep", params).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  ipg({ commit }, params) {
    return new Promise((resolve, reject) => {
      api().post("/data/ipg", params).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  mslots({ commit }, request) {
    return new Promise((resolve, reject) => {
      api().post("/data/slots", request).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  summary({ commit }, request) {
    return new Promise((resolve) => {
      api().post('/data/summary', request).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  saveChart({ commit }, data) {
    return new Promise((resolve) => {
      api().post('/user/chart/save', data).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  deleteChart({ commit }, id) {
    return new Promise((resolve) => {
      api().post('/user/chart/delete/' + id).then((response) => {
        resolve(response.data.data)
      })
    })
  },
  tepAll({ commit }, data) {
    return new Promise((resolve) => {
      api().post('/data/sankey', data).then((response) => {
        resolve(response.data)
      })
    })
  },
  monthlyTepCategory({ commit }, data) {
    return new Promise((resolve) => {
      api().post('/data/monthly-tep-category', data).then((response) => {
        resolve(response.data)
      })
    })
  },
  monthlySiteValues({ commit }, data) {
    return new Promise((resolve) => {
      api().post('/data/monthly-site-values', data).then((response) => {
        resolve(response.data)
      })
    })
  },

  filterSettings({ commit }, channels) {
    return new Promise((resolve) => {
      api().post('/data/filters', channels).then((response) => {
        resolve(response.data.data)
      })
    })
  },

  clearAllSelectedIndicatorsChannels({ commit }) {
    commit('clearAllSelectedIndicatorsChannels');
  },

}

// mutations
const mutations = {

  setDataModelId(state, dataModelId) {
    state.currentDataModelId = dataModelId;
  },

  setModelId(state, dataModelId) {
    state.dataModelId = dataModelId;
  },

  //FUNCTION: Sets A Node's Visibility in Source Tree
  setVisibilityState(state, { node, visibility }) {
    node.visible = visibility
  },

  //FUNCTION: Expands or Collapses A Node in Source Tree
  toggleExpandState(state, node) {
    node.open = !node.open
  },

  //FUNCTION: Sets Expand State Based On Value A Node in Source Tree
  setExpandState(state, { node, expand }) {
    node.open = expand
  },

  //FUNCTION: Selects or Deselects A Node in Source Tree
  toggleSelectState(state, node) {
    node.selected = !node.selected
  },

  //FUNCTION: Selects or Deselects A Node in Source Tree
  setSelectState(state, { node, select }) {
    node.selected = select
  },

  //FUNCTION: Deselects All Nodes in Source Tree
  unselectAllNodes(state) {
    if (!state.tree) return;
    state.tree.children.forEach(function s(node) {
      node.selected = false;
      node.children && node.children.forEach(s)
    });
  },

  //FUNCTION: Adds or Removes a Source in Selected Sources List
  toggleNodeInSelectedSources(state, { node, defaultMT }) {
    const index = state.selectedSources.findIndex((ss) => ss.code === node.code);
    if (index === -1) {
      state.selectedSources.push({
        name: node.name,
        code: node.code,
        type: node.type,
        dataModelId: node.data_model_id,
        ...(node.type === "channel" && { measures: defaultMT })//only includes measures field if node type is channel
      });
    } else {
      state.selectedSources.splice(index, 1);
    }
  },

  //FUNCTION: Filters the 'selectedSources' list based on current mode (MEASURES:['channel','parameter']/INDICATORS: ['indicator'])
  filterSelectedSourcesForMode(state, filters) {
    state.selectedSources = state.selectedSources.filter(s => filters.includes(s.type))
  },

  //FUNCTION: Sets the 'measures' list in a source of 'channel' type in 'selectedSources' list
  setMeasuresInSelectedSource(state, { source, measures }) {
    source.measures = [...measures];
  },

  //FUNCTION: Sets the 'selectedSources' list
  setSelectedSources(state, payload) {
    state.selectedSources = payload;
  },

  //FUNCTION: Clears the 'selectedSources' list
  clearAllSelectedSources(state) {
    state.selectedSources = [];
  },


  ////////OLD STATE MUTATIONS////////

  clear(state) {
    const initialStateData = initialState();
    Object.keys(initialStateData).forEach(key => {
      state[key] = initialStateData[key];
    });
  },

  setMeasureTypes(state, payload) {
    state.measureTypes = payload;
  },

  setSelectedMeasureTypes(state, payload) {
    state.selectedMeasureTypes = payload;
  },

  setLastFetchedChartParameters(state, params) {
    state.lastFetchedChartParameters = _.cloneDeep(params);
  },

  setLastFetchedChartData(state, data) {
    state.lastFetchedChartData = _.cloneDeep(data);
  },

  setTree(state, tree) {
    state.tree = tree
  },
}

export default {
  namespaced: true,
  state: initialState(),
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/charts.js